import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { v2GetMyRequests } from '../../services/v2/api';
import { COLORS } from '../../theme/themeTokens';
import type { V2Request } from '../../types/v2';
import { numberToAmountDisplay } from '../../utils/amountFormat';

type Filter = 'all' | 'active' | 'confirmed';

interface Props {
  onRequestPress: (request: V2Request) => void;
}

const isActive = (request: V2Request) =>
  request.status_code === 'pending' || request.status_code === 'in_process';

const formatDate = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString('en-GB', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
      }).replace(',', '').replace(/\//g, '.');
};

const statusColors = (code: V2Request['status_code']) => {
  if (code === 'confirmed') {
    return { backgroundColor: '#CFF8DE', color: '#0D803B' };
  }
  if (code === 'cancelled') {
    return { backgroundColor: '#F9DDE1', color: '#BE3244' };
  }
  return { backgroundColor: '#FFF2D4', color: '#D98200' };
};

const formatRequestAmount = (value: string) =>
  numberToAmountDisplay(value, 2)
    .replace(/,00$/, '')
    .replace(/,(\d)0$/, ',$1');

const CardOrderIcon = () => (
  <Svg width={18} height={13} viewBox="0 0 18 13" fill="none">
    <Path
      d="M16.25 0H1.25C.918 0 .601.132.366.366A1.25 1.25 0 0 0 0 1.25v10c0 .332.132.649.366.884.235.234.552.366.884.366h15c.332 0 .649-.132.884-.366.234-.235.366-.552.366-.884v-10c0-.332-.132-.649-.366-.884A1.25 1.25 0 0 0 16.25 0Zm0 1.25v1.875h-15V1.25h15Zm0 10h-15V4.375h15v6.875ZM15 9.375a.625.625 0 0 1-.625.625h-2.5a.625.625 0 1 1 0-1.25h2.5a.625.625 0 0 1 .625.625Zm-5 0a.625.625 0 0 1-.625.625h-1.25a.625.625 0 1 1 0-1.25h1.25a.625.625 0 0 1 .625.625Z"
      fill={COLORS.primaryNavy}
    />
  </Svg>
);

const CompletedOrderIcon = () => (
  <Svg width={17} height={17} viewBox="0 0 17 17" fill="none">
    <Path
      d="M11.692 5.808a.625.625 0 0 1 0 .884l-4.375 4.375a.625.625 0 0 1-.884 0L4.558 9.192a.625.625 0 1 1 .884-.884l1.433 1.433 3.933-3.933a.625.625 0 0 1 .884 0ZM16.25 8.125a8.125 8.125 0 1 1-16.25 0 8.125 8.125 0 0 1 16.25 0Zm-1.25 0a6.875 6.875 0 1 0-13.75 0 6.875 6.875 0 0 0 13.75 0Z"
      fill={COLORS.primaryNavy}
    />
  </Svg>
);

const RequestRow: React.FC<{ request: V2Request; onPress: () => void }> = ({ request, onPress }) => {
  const badge = statusColors(request.status_code);
  const amount = request.amount
    ? `${formatRequestAmount(request.amount)} ${request.currency ?? ''}`.trim()
    : '';
  return (
    <TouchableOpacity style={styles.row} activeOpacity={0.75} onPress={onPress}>
      <View style={styles.iconBox}>
        {request.type_code.includes('card') ? <CardOrderIcon /> : <CompletedOrderIcon />}
      </View>
      <View style={styles.rowBody}>
        <View style={styles.rowLine}>
          <Text style={styles.rowTitle} numberOfLines={1}>{request.type_name}</Text>
          {!!amount && <Text style={styles.amount}>{amount}</Text>}
        </View>
        <View style={styles.rowLine}>
          <View style={[styles.badge, { backgroundColor: badge.backgroundColor }]}>
            <Text style={[styles.badgeText, { color: badge.color }]}>{request.status_name}</Text>
          </View>
          <Text style={styles.date}>{formatDate(request.created_at)}</Text>
        </View>
      </View>
      <Text style={styles.chevron}>›</Text>
    </TouchableOpacity>
  );
};

/**
 * The user's orders (requests) — the Orders tab of the history screen:
 * All / Active / Confirmed filters, grouped into Active and Closed.
 */
const OrdersList: React.FC<Props> = ({ onRequestPress }) => {
  const [requests, setRequests] = useState<V2Request[]>([]);
  const [filter, setFilter] = useState<Filter>('all');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (refresh = false) => {
    refresh ? setRefreshing(true) : setLoading(true);
    setError('');
    try {
      const response = await v2GetMyRequests();
      setRequests(response.requests ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load orders');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => requests.filter((request) => {
    if (filter === 'active') {
      return isActive(request);
    }
    if (filter === 'confirmed') {
      return !isActive(request);
    }
    return true;
  }), [filter, requests]);

  const groups = useMemo(() => {
    if (filter !== 'all') {
      return [{ title: filter === 'active' ? 'Active' : 'Confirmed', rows: visible }];
    }
    return [
      { title: 'Active', rows: visible.filter(isActive) },
      { title: 'Closed', rows: visible.filter((row) => !isActive(row)) },
    ];
  }, [filter, visible]);

  return (
    <View style={styles.content}>
        <View style={styles.filters}>
          {(['all', 'active', 'confirmed'] as Filter[]).map((value) => (
            <TouchableOpacity
              key={value}
              style={[styles.filter, filter === value && styles.filterActive]}
              onPress={() => setFilter(value)}>
              <Text style={[styles.filterText, filter === value && styles.filterTextActive]}>
                {value[0].toUpperCase() + value.slice(1)}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
        {loading ? <ActivityIndicator style={styles.loader} color={COLORS.primaryNavy} /> : (
          <ScrollView
            contentContainerStyle={styles.list}
            refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => load(true)} />}>
            {!!error && <Text style={styles.error}>{error}</Text>}
            {!error && visible.length === 0 && <Text style={styles.empty}>No orders yet</Text>}
            {groups.map((group) => group.rows.length > 0 && (
              <View key={group.title}>
                <Text style={styles.sectionTitle}>{group.title}</Text>
                <View style={styles.card}>
                  {group.rows.map((request) => (
                    <RequestRow key={request.id} request={request} onPress={() => onRequestPress(request)} />
                  ))}
                </View>
              </View>
            ))}
          </ScrollView>
        )}
    </View>
  );
};

const styles = StyleSheet.create({
  // Filter row matches the Transactions tab pills (TransactionHistoryScreen):
  // borderless, filled only when selected.
  content: { flex: 1, paddingHorizontal: 16, paddingTop: 12 },
  filters: { flexDirection: 'row', gap: 4, marginBottom: 18 },
  filter: { paddingHorizontal: 14, paddingVertical: 10, borderRadius: 8, backgroundColor: COLORS.pureLight, alignItems: 'center' },
  filterActive: { backgroundColor: COLORS.steelNavy },
  filterText: { fontFamily: 'Inter', fontSize: 16, lineHeight: 22, fontWeight: '400', color: COLORS.primaryNavy },
  filterTextActive: { color: COLORS.white },
  // Clears the floating tab bar.
  list: { paddingBottom: 120 },
  loader: { marginTop: 50 },
  sectionTitle: { color: COLORS.primaryNavy, fontSize: 15, marginBottom: 14, marginTop: 3 },
  card: { backgroundColor: COLORS.white, borderWidth: 1, borderColor: COLORS.softGrey, borderRadius: 12, overflow: 'hidden', marginBottom: 18 },
  row: { minHeight: 66, flexDirection: 'row', alignItems: 'center', paddingHorizontal: 10, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: COLORS.borderMuted },
  iconBox: { width: 39, height: 39, borderWidth: 1, borderColor: COLORS.primaryNavy, borderRadius: 8, alignItems: 'center', justifyContent: 'center' },
  rowBody: { flex: 1, paddingLeft: 10 },
  rowLine: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', minHeight: 23 },
  rowTitle: { color: COLORS.primaryNavy, fontSize: 14, flex: 1, marginRight: 8 },
  amount: { color: COLORS.primaryNavy, fontSize: 14 },
  badge: { borderRadius: 10, paddingHorizontal: 8, paddingVertical: 3 },
  badgeText: { fontSize: 11 },
  date: { color: COLORS.blueSteel, fontSize: 11 },
  chevron: { color: COLORS.blueSteel, fontSize: 30, fontWeight: '200', paddingLeft: 6 },
  error: { color: '#BE3244', textAlign: 'center', marginTop: 30 },
  empty: { color: COLORS.blueSteel, textAlign: 'center', marginTop: 40 },
});

export default OrdersList;
